// =============================================================================
// useRealtimeSubscription
// =============================================================================
// PURPOSE: Generic Supabase Realtime wrapper for Postgres changes with auto-reconnect.
//
// ARCHITECTURE:
// - Subscribes to INSERT/UPDATE/DELETE events on any table
// - Automatic cleanup on unmount (prevents memory leaks)
// - Exponential backoff reconnection (3 attempts max, then gives up)
// - Returns connection status for graceful degradation (caller falls back to polling)
//
// USAGE:
// - Used by useLeaderboard for instant leaderboard updates
// - Type-safe callbacks: onInsert, onUpdate, onDelete
// - Filter expression support (e.g., 'organization_id=eq.abc-123')
//
// KEY DECISIONS:
// - Fails fast after 3 reconnect attempts (10s max delay)
// - Stores callbacks in refs to avoid recreating subscription on callback changes
// - isUnmountedRef prevents async operations after unmount (avoids React warnings)
// - queueMicrotask for initial connect (avoids synchronous setState in effect)
// =============================================================================

import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "../../lib/supabase";
import { RealtimeChannel, RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { logger } from "../../lib/logger";

export type RealtimeStatus = "connecting" | "connected" | "disconnected" | "error";

export interface RealtimeConfig<T extends Record<string, unknown>> {
  /** Unique channel name (e.g., 'leaderboard:org-123') */
  channelName: string;
  /** Table to subscribe to */
  table: string;
  /** Schema (defaults to 'public') */
  schema?: string;
  /** Filter expression (e.g., 'organization_id=eq.abc-123') */
  filter?: string;
  /** Events to listen for */
  events?: ("INSERT" | "UPDATE" | "DELETE")[];
  /** Callback when data changes */
  onInsert?: (payload: T) => void;
  onUpdate?: (payload: T, oldRecord?: T) => void;
  onDelete?: (oldRecord: T) => void;
  /** Whether subscription is enabled (default: true) */
  enabled?: boolean;
}

export interface UseRealtimeResult {
  /** Current connection status */
  status: RealtimeStatus;
  /** Whether connected and receiving updates */
  isConnected: boolean;
  /** Manually reconnect */
  reconnect: () => void;
  /** Manually disconnect */
  disconnect: () => void;
}

export function useRealtimeSubscription<T extends Record<string, unknown>>(
  config: RealtimeConfig<T>
): UseRealtimeResult {
  const {
    channelName,
    table,
    schema = "public",
    filter,
    events = ["INSERT", "UPDATE", "DELETE"],
    onInsert,
    onUpdate,
    onDelete,
    enabled = true,
  } = config;

  const [status, setStatus] = useState<RealtimeStatus>("disconnected");
  const channelRef = useRef<RealtimeChannel | null>(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 3; // Reduced from 5 - fail fast
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountedRef = useRef(false); // Track if component unmounted

  // Store callbacks in refs to avoid recreating subscription on callback changes
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete });
  useEffect(() => {
    callbacksRef.current = { onInsert, onUpdate, onDelete };
  }, [onInsert, onUpdate, onDelete]);

  const handlePayload = useCallback((payload: RealtimePostgresChangesPayload<T>) => {
    const { eventType, new: newRecord, old: oldRecord } = payload;

    switch (eventType) {
      case "INSERT":
        callbacksRef.current.onInsert?.(newRecord as T);
        break;
      case "UPDATE":
        callbacksRef.current.onUpdate?.(newRecord as T, oldRecord as T);
        break;
      case "DELETE":
        callbacksRef.current.onDelete?.(oldRecord as T);
        break;
    }
  }, []);

  const disconnect = useCallback(() => {
    if (channelRef.current) {
      supabase?.removeChannel(channelRef.current);
      channelRef.current = null;
      // Only update state and log if not unmounted
      if (!isUnmountedRef.current) {
        setStatus("disconnected");
        logger.debug(`Realtime: Disconnected from ${channelName}`);
      }
    }
  }, [channelName]);

  // Use a ref to store the connect function for reconnection scheduling
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    // Don't connect if unmounted or disabled
    if (isUnmountedRef.current || !supabase || !enabled) {
      setStatus("disconnected");
      return;
    }

    // Clean up any pending reconnection
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Clean up existing channel
    disconnect();

    setStatus("connecting");
    logger.debug(`Realtime: Connecting to ${channelName}`);

    // Build the subscription config
    const subscribeConfig: {
      event: "INSERT" | "UPDATE" | "DELETE" | "*";
      schema: string;
      table: string;
      filter?: string;
    } = {
      event: events.length === 3 ? "*" : events[0],
      schema,
      table,
    };

    if (filter) {
      subscribeConfig.filter = filter;
    }

    const channel = supabase
      .channel(channelName)
      .on("postgres_changes", subscribeConfig, handlePayload)
      .subscribe((subscriptionStatus) => {
        if (subscriptionStatus === "SUBSCRIBED") {
          setStatus("connected");
          reconnectAttempts.current = 0;
          logger.debug(`Realtime: Connected to ${channelName}`);
        } else if (subscriptionStatus === "CLOSED") {
          setStatus("disconnected");
        } else if (subscriptionStatus === "CHANNEL_ERROR") {
          setStatus("error");

          // Don't log errors or retry if already unmounted
          if (isUnmountedRef.current) {
            return;
          }

          // Attempt reconnection with exponential backoff (only if not unmounted)
          if (reconnectAttempts.current < maxReconnectAttempts) {
            reconnectAttempts.current++;
            const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000); // Max 10s
            logger.debug(
              `Realtime: Channel error on ${channelName}, retrying in ${delay}ms (${reconnectAttempts.current}/${maxReconnectAttempts})`
            );
            reconnectTimeoutRef.current = setTimeout(() => {
              if (!isUnmountedRef.current) {
                connectRef.current();
              }
            }, delay);
          } else {
            // Give up after max attempts - will fall back to polling
            logger.debug(
              `Realtime: Gave up on ${channelName} after ${maxReconnectAttempts} attempts, using polling fallback`
            );
          }
        }
      });

    channelRef.current = channel;
  }, [channelName, table, schema, filter, events, enabled, handlePayload, disconnect]);

  // Keep connectRef in sync with the latest connect function
  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    isUnmountedRef.current = false;
    reconnectAttempts.current = 0; // Reset attempts on fresh mount

    if (enabled) {
      // Defer connect to avoid synchronous setState within effect
      queueMicrotask(() => {
        if (!isUnmountedRef.current) {
          connect();
        }
      });
    }

    return () => {
      // Mark as unmounted FIRST to prevent any async operations
      isUnmountedRef.current = true;

      // Clear any pending reconnection timers
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Disconnect the channel
      disconnect();
    };
  }, [enabled, connect, disconnect]);

  return {
    status,
    isConnected: status === "connected",
    reconnect,
    disconnect,
  };
}
